// frontend/client/src/CampusFeedPage.js
import React, { useEffect, useState } from "react";
import "./CampusFeedPage.css";

function CampusFeedPage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);

  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [csrfToken, setCsrfToken] = useState("");
  const [commentInputs, setCommentInputs] = useState({}); // postId -> text

  const loadFeed = () => {
    setLoading(true);
    setError(null);

    fetch("/api/campus-feed", {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .catch(() => ({}))
            .then((data) => {
              throw new Error(data.error || `status_${res.status}`);
            });
        }
        return res.json();
      })
      .then((data) => {
        setUser(data.user);
        setPosts(data.posts || []);
        setCsrfToken(data.csrfToken || "");
        setLoading(false);
      })
      .catch((err) => {
        console.error("campus feed error", err);
        setError(err.message || "unknown_error");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadFeed();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = (text || "").trim();

    if (!trimmed && !imageFile) return;
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("text", trimmed);
    if (imageFile) {
      formData.append("image", imageFile);
    }
    formData.append("csrfToken", csrfToken || "");

    fetch("/api/campus-feed", {
      method: "POST",
      credentials: "include",
      body: formData,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) {
          setError(data.error || "upload_failed");
          return;
        }
        setText("");
        setImageFile(null);
        return fetch("/api/campus-feed", { credentials: "include" });
      })
      .then((res) => (res ? res.json() : null))
      .then((data) => {
        if (data) {
          setUser(data.user);
          setPosts(data.posts || []);
          setCsrfToken(data.csrfToken || "");
        }
      })
      .catch((err) => {
        console.error("create post (with image) error", err);
        setError("network_error");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleToggleLike = (postId) => {
    if (!csrfToken) return;
    const body = new URLSearchParams({
      postId: String(postId),
      csrfToken,
      returnTo: "/campus-feed",
    }).toString();

    fetch("/like-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;
        setPosts((prev) =>
          prev.map((p) =>
            p.id === postId
              ? {
                  ...p,
                  likedByMe: data.liked,
                  likeCount:
                    typeof data.likeCount === "number"
                      ? data.likeCount
                      : p.likeCount,
                }
              : p
          )
        );
      })
      .catch((err) => {
        console.error("like error", err);
      });
  };

  const handleDeletePost = (postId) => {
    if (!csrfToken) return;
    if (!window.confirm("Bu postu silmek istediğine emin misin?")) return;

    const body = new URLSearchParams({
      id: String(postId),
      csrfToken,
      returnTo: "/campus-feed",
    }).toString();

    fetch("/delete-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;
        setPosts((prev) => prev.filter((p) => p.id !== postId));
      })
      .catch((err) => {
        console.error("delete post error", err);
      });
  };

  const handleCommentChange = (postId, value) => {
    setCommentInputs((prev) => ({
      ...prev,
      [postId]: value,
    }));
  };

  const handleSubmitComment = (e, postId) => {
    e.preventDefault();
    if (!csrfToken) return;

    const raw = commentInputs[postId] || "";
    const trimmed = raw.trim();
    if (!trimmed) return;

    const body = new URLSearchParams({
      postId: String(postId),
      comment: trimmed,
      csrfToken,
      returnTo: "/campus-feed",
    }).toString();

    fetch("/comment-post", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest",
      },
      body,
    })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) return;

        setCommentInputs((prev) => ({
          ...prev,
          [postId]: "",
        }));

        setPosts((prev) =>
          prev.map((p) => {
            if (p.id !== postId) return p;

            const newCount =
              typeof data.commentCount === "number"
                ? data.commentCount
                : p.commentCount + 1;

            const newComment = {
              authorName: data.authorName || "You",
              text: data.text || trimmed,
            };

            return {
              ...p,
              commentCount: newCount,
              comments: [...(p.comments || []), newComment],
            };
          })
        );
      })
      .catch((err) => {
        console.error("comment error", err);
      });
  };

  if (loading)
    return (
      <div style={{ padding: 24, color: "#fff" }}>Loading campus feed...</div>
    );
  if (error && !posts.length && !user)
    return <div style={{ padding: 24, color: "#fff" }}>Error: {error}</div>;
  if (!user)
    return <div style={{ padding: 24, color: "#fff" }}>No user</div>;

  return (
    <div className="campus-container">
      {/* TOPBAR */}
      <header className="topbar">
        <div style={{ fontWeight: 700, fontSize: 18 }}>GRADA · Campus feed</div>
        <button
          className="profile-btn"
          type="button"
          onClick={() => (window.location.href = "/profile")}
        >
          <img
            src="/avatar"
            alt="Avatar"
            className="avatar"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <div>
            <div className="profile-name">{user.name}</div>
            <div className="profile-sub">{user.university}</div>
          </div>
        </button>
      </header>

      {/* SHARE CARD */}
      <section className="card">
        <h2 className="card-title">Share something with your campus</h2>
        {error && <div className="feed-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <textarea
            className="campus-textarea"
            placeholder="What&apos;s going on on campus?"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />

          <input
            type="file"
            className="campus-input"
            accept="image/*"
            onChange={(e) => {
              const file =
                e.target.files && e.target.files.length > 0
                  ? e.target.files[0]
                  : null;
              setImageFile(file);
            }}
          />

          <br />
          <button
            type="submit"
            className="btn"
            disabled={submitting || (!text.trim() && !imageFile)}
          >
            {submitting ? "Posting..." : "Post"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => (window.location.href = "/dashboard")}
          >
            Back to main page
          </button>
        </form>
      </section>

      {/* POSTS */}
      <section>
        {posts.map((p) => {
          const likeLabel = p.likedByMe ? "Unlike" : "Like";
          const commentValue = commentInputs[p.id] || "";

          return (
            <div className="post" id={`post-${p.id}`} key={p.id}>
              <div className="post-header">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <img
                    src={
                      p.authorEmail
                        ? `/post-author-avatar?email=${encodeURIComponent(
                            p.authorEmail
                          )}`
                        : "/avatar"
                    }
                    className="post-avatar"
                    alt="Avatar"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  <div>
                    <div className="post-author">{p.authorName}</div>
                    <div className="post-meta">
                      {p.university} · {p.createdAt}
                    </div>
                  </div>
                </div>

                {p.deletable && (
                  <button
                    type="button"
                    className="post-delete-btn"
                    onClick={() => handleDeletePost(p.id)}
                  >
                    Delete
                  </button>
                )}
              </div>

              <div className="post-body">
                <p>{p.text}</p>
                {p.hasImage && (
                  <div className="post-image-wrapper">
                    <img
                      src={`/post-image?id=${p.id}`}
                      className="post-image"
                      alt="post"
                    />
                  </div>
                )}
              </div>

              <div className="post-actions">
                <button
                  type="button"
                  className="btn-like"
                  onClick={() => handleToggleLike(p.id)}
                >
                  {likeLabel} ({p.likeCount})
                </button>
                <div className="comment-count">
                  {p.commentCount} comments
                </div>
              </div>

              {/* YORUM BLOĞU – eski HTML ile birebir sıra:
                  1) input (form)
                  2) yorum satırları */}
              <div className="post-comments">
                <form
                  className="comment-form"
                  onSubmit={(e) => handleSubmitComment(e, p.id)}
                >
                  <input
                    type="text"
                    className="comment-input"
                    placeholder="Write a comment..."
                    value={commentValue}
                    onChange={(e) =>
                      handleCommentChange(p.id, e.target.value)
                    }
                  />
                </form>

                {(p.comments || []).map((c, idx) => (
                  <div className="comment-row" key={idx}>
                    <span className="comment-author">
                      {c.authorName || "Anon"}:
                    </span>
                    <span className="comment-text">{c.text}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default CampusFeedPage;
